/**
 * What you can do to a value without leaving the grid.
 *
 * A spreadsheet answers "show me only these" by right-clicking the cell that
 * already holds the value, rather than by opening a filter builder and typing
 * the value back in. Every action here is scoped to what was clicked, so the
 * menu never asks a question the click has already answered.
 *
 * The full builder still lives on the column header, and is the place for a
 * condition the grid is not already showing you. This menu deliberately does
 * not reproduce it: what it adds is the one click for the value under the
 * cursor.
 *
 * Nothing here changes the file. Filtering and sorting are ways of reading;
 * the destructive operations stay on the toolbar, where they are further from
 * an accidental right-click and closer to their confirmations.
 */
import { useState } from 'react';
import { Button, Group, Menu, Modal, Stack, Text, TextInput } from '@mantine/core';
import {
  IconCopy,
  IconFilter,
  IconFilterOff,
  IconFilterX,
  IconMathGreater,
  IconMathLower,
  IconSearch,
  IconSortAscending,
  IconSortDescending,
} from '@tabler/icons-react';
import type { SheetFilter } from '../api/queries';

export type CellMenuTarget = {
  x: number;
  y: number;
  col: string;
  value: string;
  /** Every distinct selected cell, when the click landed inside a selection. */
  selection: { col: string; value: string }[];
};

/** A value short enough to sit in a menu item, with the rest left implied. */
function short(value: string): string {
  if (value === '') return '(empty)';
  return value.length > 24 ? `${value.slice(0, 24)}…` : value;
}

/**
 * The number this cell holds, when it plainly holds one.
 *
 * Deliberately stricter than the server, which strips every character that is
 * not a digit, sign or point before comparing. That tolerance is right when
 * someone has typed a bound into the filter builder — "£1,200" should mean
 * 1200 — but wrong as a test of whether to offer the option at all: it would
 * read "2026-09-02" as 20260902 and cheerfully offer to find rows greater
 * than a date that is not a number. Offering it only for values that already
 * look like numbers keeps the menu honest about what it will do.
 */
export function asNumber(raw: string): number | null {
  const plain = /^\s*[-+]?[£$€]?\s*\d{1,3}(?:,\d{3})*(?:\.\d+)?\s*$/.test(raw)
    || /^\s*[-+]?[£$€]?\s*\d+(?:\.\d+)?\s*$/.test(raw);
  if (!plain) return null;
  const n = Number(raw.replace(/[^0-9.+-]/g, ''));
  return Number.isFinite(n) ? n : null;
}

/**
 * A blank cell is filtered with `empty`, not `eq ''`.
 *
 * The two match the same rows, but only one of them reads as an intention
 * when it turns up as a filter chip an hour later.
 */
function isFor(col: string, value: string): SheetFilter {
  return value === '' ? { col, op: 'empty' } : { col, op: 'eq', value };
}

function isNotFor(col: string, value: string): SheetFilter {
  return value === '' ? { col, op: 'not_empty' } : { col, op: 'ne', value };
}

export function SheetCellMenu({
  target,
  hasFilters,
  onClose,
  onFilter,
  onSort,
  onClearFilters,
  onCopy,
}: {
  /** Null when closed. Its position is where the click landed. */
  target: CellMenuTarget | null;
  hasFilters: boolean;
  onClose: () => void;
  onFilter: (filters: SheetFilter[]) => void;
  onSort: (col: string, dir: 'asc' | 'desc') => void;
  onClearFilters: () => void;
  onCopy: (value: string) => void;
}) {
  /*
    The "contains" prompt, holding its own copy of what was right-clicked.

    It has to outlive the menu: choosing the item closes the menu, and the
    menu closing is what clears `target`. Copying the column and the starting
    text here is what lets the box stay open afterwards.
  */
  const [asking, setAsking] = useState<{ col: string } | null>(null);
  const [needle, setNeedle] = useState('');

  const number = target ? asNumber(target.value) : null;
  // More than one distinct value selected makes "these" a different question
  // from "this", and worth offering separately.
  const many = (target?.selection.length ?? 0) > 1;

  function applyContains() {
    const text = needle.trim();
    if (!asking || text === '') return;
    onFilter([{ col: asking.col, op: 'contains', value: text }]);
    setAsking(null);
  }

  return (
    <>
      {target && (
        <Menu
          opened
          onClose={onClose}
          position="bottom-start"
          offset={0}
          shadow="md"
          width={260}
          withinPortal
        >
          <Menu.Target>
            {/*
              The click, given a size so a menu can hang off it. Fixed rather
              than absolute because the coordinates come from the viewport, and
              the grid it was clicked in scrolls.
            */}
            <div
              style={{
                position: 'fixed',
                left: target.x,
                top: target.y,
                width: 1,
                height: 1,
                pointerEvents: 'none',
              }}
            />
          </Menu.Target>

          <Menu.Dropdown>
            <Menu.Label>
              <Text size="xs" c="dimmed" style={{ textTransform: 'none' }} truncate>
                {target.col} = {short(target.value)}
              </Text>
            </Menu.Label>

            <Menu.Item
              leftSection={<IconFilter size={14} />}
              onClick={() => {
                onFilter([isFor(target.col, target.value)]);
                onClose();
              }}
            >
              Filter to this value
            </Menu.Item>

            <Menu.Item
              leftSection={<IconFilterX size={14} />}
              onClick={() => {
                onFilter([isNotFor(target.col, target.value)]);
                onClose();
              }}
            >
              Exclude this value
            </Menu.Item>

            {many && (
              <Menu.Item
                leftSection={<IconFilter size={14} />}
                onClick={() => {
                  onFilter(target.selection.map((c) => isFor(c.col, c.value)));
                  onClose();
                }}
              >
                Filter to the {target.selection.length} selected values
              </Menu.Item>
            )}

            {/*
              Only for a value that is plainly a number. On text it would
              compare something the column does not contain.
            */}
            {number !== null && (
              <>
                <Menu.Divider />
                <Menu.Item
                  leftSection={<IconMathGreater size={14} />}
                  onClick={() => {
                    onFilter([{ col: target.col, op: 'gt', value: String(number) }]);
                    onClose();
                  }}
                >
                  Greater than {short(target.value)}
                </Menu.Item>
                <Menu.Item
                  leftSection={<IconMathLower size={14} />}
                  onClick={() => {
                    onFilter([{ col: target.col, op: 'lt', value: String(number) }]);
                    onClose();
                  }}
                >
                  Less than {short(target.value)}
                </Menu.Item>
              </>
            )}

            <Menu.Divider />

            <Menu.Item
              leftSection={<IconSearch size={14} />}
              onClick={() => {
                // Prefilled with what was clicked, so the common case is
                // Enter and the box is only there to narrow it.
                setNeedle(target.value);
                setAsking({ col: target.col });
                onClose();
              }}
            >
              Contains…
            </Menu.Item>

            <Menu.Item
              leftSection={<IconSortAscending size={14} />}
              onClick={() => {
                onSort(target.col, 'asc');
                onClose();
              }}
            >
              Sort A–Z by {target.col}
            </Menu.Item>
            <Menu.Item
              leftSection={<IconSortDescending size={14} />}
              onClick={() => {
                onSort(target.col, 'desc');
                onClose();
              }}
            >
              Sort Z–A by {target.col}
            </Menu.Item>

            <Menu.Divider />

            <Menu.Item
              leftSection={<IconCopy size={14} />}
              disabled={target.value === ''}
              onClick={() => {
                onCopy(target.value);
                onClose();
              }}
            >
              Copy value
            </Menu.Item>

            {hasFilters && (
              <Menu.Item
                leftSection={<IconFilterOff size={14} />}
                onClick={() => {
                  onClearFilters();
                  onClose();
                }}
              >
                Clear all filters
              </Menu.Item>
            )}
          </Menu.Dropdown>
        </Menu>
      )}

      <Modal
        opened={asking !== null}
        onClose={() => setAsking(null)}
        title={asking ? `Rows where ${asking.col} contains` : ''}
        size="sm"
      >
        <Stack>
          <TextInput
            data-autofocus
            label="Text to look for"
            description="Upper and lower case are treated as the same."
            value={needle}
            onChange={(e) => setNeedle(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') applyContains();
            }}
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setAsking(null)}>
              Cancel
            </Button>
            <Button disabled={needle.trim() === ''} onClick={applyContains}>
              Apply filter
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
